import { z } from 'zod';

export const optionSchema = z.object({
  id: z.string().min(1).max(40),
  name: z.string().min(1).max(80),
  priceDeltaCents: z.number().int().min(0).max(100_000),
  isDefault: z.boolean().optional(),
});

export const optionGroupSchema = z.object({
  id: z.string().min(1).max(40),
  name: z.string().min(1).max(80),
  type: z.enum(['single', 'multi']),
  required: z.boolean(),
  maxSelections: z.number().int().min(1).max(30).optional(),
  options: z.array(optionSchema).min(1).max(30),
});

export const itemConfigSchema = z.object({
  groups: z.array(optionGroupSchema).max(10).default([]),
  tags: z.array(z.string().max(30)).max(10).optional(),
  popular: z.boolean().optional(),
});

export type MenuOption = z.infer<typeof optionSchema>;
export type OptionGroup = z.infer<typeof optionGroupSchema>;
export type ItemConfig = z.infer<typeof itemConfigSchema>;

/** What the customer picked: groupId -> option ids. */
export type Selections = Record<string, string[]>;

export interface PricedSelection {
  groupId: string;
  groupName: string;
  optionId: string;
  optionName: string;
  priceDeltaCents: number;
}

/** Why a selection was rejected; formatted in either language by selectionMessage(). */
export type SelectionIssue =
  | { kind: 'unknown_group'; id: string }
  | { kind: 'single_only'; group: string }
  | { kind: 'required'; group: string }
  | { kind: 'max'; group: string; max: number }
  | { kind: 'unknown_option'; id: string };

/** `name` lets the caller translate group names coming from the demo menu. */
export function selectionMessage(issue: SelectionIssue, lang: 'en' | 'fr', name: (s: string) => string = (s) => s): string {
  const fr = lang === 'fr';
  switch (issue.kind) {
    case 'unknown_group':
      return fr ? `Groupe d'options inconnu: ${issue.id}` : `Unknown option group: ${issue.id}`;
    case 'single_only':
      return fr ? `Un seul choix permis pour ${name(issue.group)}` : `Only one choice allowed for ${name(issue.group)}`;
    case 'required':
      return fr ? `Choix requis: ${name(issue.group)}` : `Choice required: ${name(issue.group)}`;
    case 'max':
      return fr ? `Maximum ${issue.max} choix pour ${name(issue.group)}` : `Up to ${issue.max} choices for ${name(issue.group)}`;
    case 'unknown_option':
      return fr ? `Option inconnue: ${issue.id}` : `Unknown option: ${issue.id}`;
  }
}

export class SelectionError extends Error {
  constructor(public issue: SelectionIssue) {
    super(selectionMessage(issue, 'fr'));
  }
}

/**
 * Validates selections against an item's config and returns the unit price plus the
 * normalized list of chosen options. Used by the web (display) and the API (authoritative).
 */
export function priceItem(
  basePriceCents: number,
  config: ItemConfig,
  selections: Selections = {},
): { unitPriceCents: number; options: PricedSelection[] } {
  const chosen: PricedSelection[] = [];
  for (const key of Object.keys(selections)) {
    if (!config.groups.some((g) => g.id === key)) throw new SelectionError({ kind: 'unknown_group', id: key });
  }
  for (const group of config.groups) {
    let ids = [...new Set(selections[group.id] ?? [])];
    if (ids.length === 0 && group.type === 'single') {
      const def = group.options.find((o) => o.isDefault);
      if (def) ids = [def.id];
    }
    if (group.type === 'single' && ids.length > 1) throw new SelectionError({ kind: 'single_only', group: group.name });
    if (group.required && ids.length === 0) throw new SelectionError({ kind: 'required', group: group.name });
    if (group.maxSelections && ids.length > group.maxSelections)
      throw new SelectionError({ kind: 'max', group: group.name, max: group.maxSelections });
    for (const id of ids) {
      const opt = group.options.find((o) => o.id === id);
      if (!opt) throw new SelectionError({ kind: 'unknown_option', id });
      chosen.push({
        groupId: group.id,
        groupName: group.name,
        optionId: opt.id,
        optionName: opt.name,
        priceDeltaCents: opt.priceDeltaCents,
      });
    }
  }
  const unitPriceCents = basePriceCents + chosen.reduce((s, o) => s + o.priceDeltaCents, 0);
  return { unitPriceCents, options: chosen };
}

/** Lowest possible price ("À partir de"). */
export function startingPrice(basePriceCents: number, config: ItemConfig): number {
  let price = basePriceCents;
  for (const g of config.groups) {
    if (g.required) price += Math.min(...g.options.map((o) => o.priceDeltaCents));
  }
  return price;
}

export function defaultSelections(config: ItemConfig): Selections {
  const s: Selections = {};
  for (const g of config.groups) {
    const def = g.options.find((o) => o.isDefault) ?? (g.required && g.type === 'single' ? g.options[0] : undefined);
    if (def) s[g.id] = [def.id];
  }
  return s;
}
